import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { toggleModal, toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { changeFormField, changeFormFieldValue, removeFieldError, setFieldError, setNewForm, updateFormProperties } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Text } from 'apps/auth/components/common/Text/Text';
import NewInputSms from 'apps/auth/components/form/NewInputSms';
import Form from 'apps/auth/components/form-multi/Form';
import { smsConfirmationToFields } from 'apps/auth/components/form-multi/utils';
import { NEW_USER, PASSCODE_SCREEN } from 'apps/auth/components/registration/constants';
import ResendCodePopup from 'apps/auth/components/registration/ResendCodePopup';
import DuplicatedRegistrationRestrictionModal from 'apps/auth/components/utils/DuplicatedRegistrationRestrictionModal';
import Spinner from 'apps/auth/components/utils/Spinner/Spinner';
import { CODE_ENTERED, CODE_ENTERED_WRONG, DID_NOT_GET_CODE_BUTTON_CLICKED, ENTER_CODE_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { COUNTRY_OF_RESIDENCE, FROM } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { SIGNUP_AMPLITUDE, LOGIN_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { LOGIN_REGISTRATION_FORM_NAME, SMS_CONFIRMATION_FORM_NAME } from 'apps/auth/constants/formName';
import { BIRTHDAY_CHECK_ACTION, LOGIN } from 'apps/auth/constants/UrlConstants';
import { CANADA, USA } from 'apps/auth/constants/WellKnownCountries';
import { ERR_DUPLICATED_REGISTRATION } from 'apps/auth/constants/WellKnownErrorCodes';
import { smsConfirmation } from 'apps/auth/helpers/customPropTypes';
import update from 'apps/auth/helpers/Immutability';
import { addZero, resolveArguments } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { useLibPhonenumberFormatter } from 'apps/auth/hooks/useLibPhonenumberFormatter';

import styles from './NewSmsConfirmation.module.css';

const NewSmsConfirmation = (props) => {
    const RESET_INTERVAL = 44;
    const interval = props.smsConfirmation?.resendTime || RESET_INTERVAL;
    const minutes = Math.floor(interval / 60);
    const seconds = interval - minutes * 60;
    const { phone, userCountryName } = useAuth();
    const [resentTime, setResetTime] = useState(`${addZero(minutes)}:${addZero(seconds)}`);
    const [showResend, setShowResend] = useState(false);
    const [isExceedLimitsError, setIsExceedLimitsError] = useState(false);
    const submitRef = useRef(null);
    const resetInterval = useRef(interval);
    const { headerGrayTitle, headerTitle, smsResendTitle, smsResendBtn, resendCodePopupText, didntGetCode, codeResent, termsOfUseCode, contactSupport } =
        usePageMessages();
    const { hideButton, phoneCountryId, phoneCountry, authType } = useExtra();
    const fields = useFields(SMS_CONFIRMATION_FORM_NAME);
    const confirmAction = '/confirm.action';
    const resendAction = '/resend.action';
    const showTerms = phoneCountryId !== USA && phoneCountryId !== CANADA;
    const isNewUser = authType === NEW_USER;
    const sendEvent = !props.isProfileFlow;
    const { ready, formatIntlPhoneNumber } = useLibPhonenumberFormatter();
    const formattedNumber = ready ? formatIntlPhoneNumber(phone, userCountryName) : `+${phone}`;

    useEffect(() => {
        if (sendEvent) {
            amplitude.getInstance().logEvent(ENTER_CODE_SCREEN_SHOWN, {
                [COUNTRY_OF_RESIDENCE]: phoneCountry?.nameEn,
                [FROM]: isNewUser ? SIGNUP_AMPLITUDE : LOGIN_AMPLITUDE,
            });
        }
    }, []);

    useEffect(() => {
        if (!props.declineResendSms) {
            const tick = () => {
                if (resetInterval.current > 0) {
                    resetInterval.current -= 1;
                    calculateTime(resetInterval.current, setResetTime);
                } else {
                    setShowResend(true);
                    const newTime = props.resetInterval || interval;
                    calculateTime(newTime, setResetTime);
                }
            };

            const id = setInterval(tick, 1000);
            return () => clearInterval(id);
        }
    }, [props.declineResendSms, props.resetInterval]);

    useEffect(() => {
        resetInterval.current = interval;
        props.setNewForm(SMS_CONFIRMATION_FORM_NAME, smsConfirmationToFields(props.smsConfirmation, props.redirectUrl));
    }, [props.smsConfirmation, props.redirectUrl]);

    useEffect(() => {
        const currentCode = fields?.code?.value;
        if (currentCode && currentCode.length === props.smsLength && !fields?.code?.isError) {
            submitRef.current.click();
        }
    }, [fields?.code?.value]);

    useEffect(() => {
        if (hideButton) {
            setIsExceedLimitsError(true);
        }
    }, [hideButton]);

    const calculateTime = (value, setResetTime) => {
        const min = Math.floor(value / 60);
        const sec = value - min * 60;
        setResetTime(`${addZero(min)}:${addZero(sec)}`);
    };

    const submitHandler = (fields, json) => {
        if (json?.extra?.smsConfirmation) {
            if (json?.extra?.smsConfirmation?.objectType === 'VERIFY_KNOW_USER_INFO') {
                props.updateFormProperties(SMS_CONFIRMATION_FORM_NAME, BIRTHDAY_CHECK_ACTION);
                return;
            }
            resetInterval.current = json?.extra?.smsConfirmation?.resendTime || RESET_INTERVAL;
        }
        if (json.messages) {
            props.updatePageMessages(json.messages);
        }

        if (json?.extra?.createPasscode) {
            props.setScreen(PASSCODE_SCREEN);
        }
        if (json?.errors) {
            Object.keys(json.errors).forEach((name) => {
                if (json.errors[name]['code'] === ERR_DUPLICATED_REGISTRATION) {
                    props.toggleModal(true, <DuplicatedRegistrationRestrictionModal url={LOGIN} />, false);
                } else {
                    if (sendEvent) {
                        amplitude.getInstance().logEvent(CODE_ENTERED_WRONG, { [COUNTRY_OF_RESIDENCE]: phoneCountry?.nameEn });
                    }
                    props.setFieldError(SMS_CONFIRMATION_FORM_NAME, name, json.errors[name]);
                }
            });
        }
        if (props.isProfileFlow && json.extra?.redirectUrl && json.messages?.successMessage) {
            props.modalRef.current?.close();
            props.toggleModalWithTimer(true, json.messages.successMessage, () => {
                window.location.href = json.extra.redirectUrl;
            });
        }
        props.updateFormProperties(SMS_CONFIRMATION_FORM_NAME, confirmAction, true);
    };

    const clearErrorAndValue = (oldField) => {
        const newFieldCode = update(oldField, {
            value: { $set: null },
            isError: { $set: false },
            errorMessage: { $set: null },
        });
        props.changeFormField(SMS_CONFIRMATION_FORM_NAME, newFieldCode);
    };

    const resendClick = (e) => {
        e.preventDefault();

        const { value, isError } = fields?.code;
        if (value || isError) {
            const fieldCode = fields?.code;
            clearErrorAndValue(fieldCode);
        }
        if (props.customResendAction) {
            props.customResendAction();
            resetInterval.current = interval;
            setShowResend(false);
        } else {
            props.updateFormProperties(SMS_CONFIRMATION_FORM_NAME, resendAction, false).then(() => {
                submitRef.current.click();
                props.toggleModalWithTimer(true, codeResent);
                resetInterval.current = interval;
                setShowResend(false);
            });
        }
    };

    const beforeRedirect = () => {
        if (props.isRegistration) {
            amplitude.getInstance().logEvent(CODE_ENTERED, { [COUNTRY_OF_RESIDENCE]: phoneCountry?.nameEn });
        }
    };
    const getSubtitleText = () => {
        if (props.headerGrayText) {
            return props.headerGrayText;
        } else {
            const phoneNumber = props.formattedNumber || formattedNumber;
            return resolveArguments(headerGrayTitle, phoneNumber);
        }
    };

    if (!fields?.code) {
        return <Spinner forceShow={true} />;
    }
    return (
        <>
            <HeaderPanel title={props.headerText || headerTitle} subtitle={getSubtitleText()} />
            <Form
                formName={SMS_CONFIRMATION_FORM_NAME}
                additionalFormNames={[LOGIN_REGISTRATION_FORM_NAME]}
                onSubmitHandler={submitHandler}
                beforeRedirect={beforeRedirect}
                submitByEnter={false}
                getCanonicalPath={true}
                action={confirmAction}
                checkNotVisibleField={false}
            >
                <Row flex justify="center" indent={!fields?.code?.isError ? 'adaptive-48-64' : '8px'}>
                    <NewInputSms
                        field={fields?.code}
                        mask={'9'.repeat(props.smsLength)}
                        formName={SMS_CONFIRMATION_FORM_NAME}
                        isNewInput
                        isSmsInput
                        smsLength={props.smsLength}
                        changeFormField={props.changeFormField}
                        isError={fields?.code?.isError}
                        removeFieldError={props.removeFieldError}
                        changeFormFieldValue={props.changeFormFieldValue}
                    />
                </Row>
                {fields?.code?.isError && (
                    <Row justify="center" flex>
                        <Text size="secondary" color="error">
                            {fields?.code?.errorMessage}
                        </Text>
                    </Row>
                )}
                <Row indent="adaptive-32-48">
                    <Button
                        variant="row"
                        caption={didntGetCode}
                        textCenter
                        negativeMargin
                        onClick={(e) => {
                            e.preventDefault();
                            if (sendEvent) {
                                amplitude.getInstance().logEvent(DID_NOT_GET_CODE_BUTTON_CLICKED, { [COUNTRY_OF_RESIDENCE]: phoneCountry?.nameEn });
                            }
                            props.popupRef.current.open(
                                <ResendCodePopup
                                    popupRef={props.popupRef}
                                    text={resendCodePopupText}
                                    smsResendBtn={smsResendBtn}
                                    contactSupport={contactSupport}
                                    resendClick={resendClick}
                                    showResend={showResend}
                                    resentTime={resentTime}
                                    smsResendText={props.smsResendText}
                                    smsResendTitle={smsResendTitle}
                                    resetInterval={resetInterval}
                                    isExceedLimitsError={isExceedLimitsError}
                                    calculateTime={calculateTime}
                                    country={phoneCountry}
                                />,
                            );
                        }}
                    />
                </Row>
                {props.isRegistration && showTerms && (
                    <div className={styles.terms}>
                        <DangerouslyHtml
                            type={'text'}
                            size="secondary"
                            color="secondary"
                            data-testid="sms-confirmation-p-text-offer"
                            variant="paragraph"
                            textAlign={'center'}
                            dangerouslyHtml={termsOfUseCode}
                        />
                    </div>
                )}
                <Button type="submit" variant="hidden" ref={submitRef} />
            </Form>
        </>
    );
};

NewSmsConfirmation.propTypes = {
    smsConfirmation: smsConfirmation.isRequired,
    redirectUrl: PropTypes.string,
    smsLength: PropTypes.number,
    resetInterval: PropTypes.number,
    termsOfUseCode: PropTypes.string,
    headerText: PropTypes.string,
    headerGrayText: PropTypes.string,
    formattedNumber: PropTypes.string,
    customResendAction: PropTypes.func,
};

export default connect(null, {
    setNewForm,
    setFieldError,
    removeFieldError,
    updateFormProperties,
    changeFormField,
    changeFormFieldValue,
    updatePageMessages,
    toggleModal,
    toggleModalWithTimer,
})(NewSmsConfirmation);
